import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { Eye, EyeOff } from "lucide-react";
import WelcomeLoader from "@/components/WelcomeLoader";

type UserSafe = {
  id: string;
  name?: string | null;
  email: string;
  generatedId?: string | null;
  verified?: boolean;
  idUploaded?: boolean;
  phone?: string | null;
  wallets?: Record<string, string> | null;
};

export default function Auth() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"login" | "signup">("login");

  // Login form fields
  const [loginIdentifier, setLoginIdentifier] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  // Signup form fields
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupGeneratedId, setSignupGeneratedId] = useState("");
  const [signupLoading, setSignupLoading] = useState(false);

  const [currentUser, setCurrentUser] = useState<UserSafe | null>(null);
  const [showWelcomeLoader, setShowWelcomeLoader] = useState(false);

  // Load user from sessionStorage on mount
  useEffect(() => {
    const loadUserFromSession = () => {
      console.log("[Auth] Loading user from sessionStorage...");
      
      const userDataStr = sessionStorage.getItem("userData");
      const userEmail = sessionStorage.getItem("userEmail");
      
      if (userDataStr) {
        try {
          const userData = JSON.parse(userDataStr);
          console.log("[Auth] Found user in sessionStorage:", userData.email);
          setCurrentUser(userData);
          return;
        } catch (e) {
          console.error("[Auth] Failed to parse userData from sessionStorage:", e);
        }
      }
      
      // Fallback to localStorage (deprecated) - for migration
      const legacyUser = localStorage.getItem("currentUser");
      if (legacyUser) {
        try {
          const userData = JSON.parse(legacyUser);
          console.log("[Auth] Found user in localStorage (deprecated):", userData.email);
          setCurrentUser(userData);
          
          // Migrate to sessionStorage
          sessionStorage.setItem("userEmail", userData.email);
          sessionStorage.setItem("userData", JSON.stringify(userData));
          localStorage.removeItem("currentUser");
        } catch (e) {
          console.error("[Auth] Failed to parse legacy user:", e);
        }
      }
    };

    loadUserFromSession();
  }, []);

  // Update sessionStorage when currentUser changes
  useEffect(() => {
    if (currentUser) {
      sessionStorage.setItem("userEmail", currentUser.email);
      sessionStorage.setItem("userData", JSON.stringify(currentUser));
    } else {
      sessionStorage.removeItem("userEmail");
      sessionStorage.removeItem("userData");
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) setShowWelcomeLoader(true);
  }, [currentUser]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginIdentifier || !loginPassword) {
      return toast({ title: "MISSING FIELDS", description: "Please provide email/ID and password" });
    }
    setLoginLoading(true);
    try {
      const isEmail = loginIdentifier.includes("@");
      const payload = isEmail
        ? { email: loginIdentifier.toLowerCase(), password: loginPassword }
        : { generatedId: loginIdentifier.toUpperCase(), password: loginPassword };

      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: 'include',  // 🔐 Allow cookies to be sent and stored
        body: JSON.stringify(payload),
      });

      const text = await res.text();
      const data = text ? JSON.parse(text) : null;

      if (!res.ok) {
        const errorCode = data?.code;
        let errorMsg = data?.error || "LOGIN FAILED";

        if (errorCode === "USER_NOT_FOUND") errorMsg = "Email/ID not registered. Please sign up first.";
        else if (errorCode === "INVALID_PASSWORD") errorMsg = "Email/ID or password is incorrect.";
        else if (res.status === 500) errorMsg = "Server error. Please try again later.";

        throw new Error(errorMsg);
      }

      // 🔐 Do NOT store auth data in sessionStorage/localStorage
      // Session is now handled by the HttpOnly cookie automatically.

      // Clear all auth storage for security
      sessionStorage.removeItem("userEmail");
      sessionStorage.removeItem("userData");
      localStorage.removeItem("currentUser");

      window.dispatchEvent(new Event("userUpdated"));
      toast({ title: "WELCOME BACK", description: `SIGNED IN AS ${user.email}` });
      navigate("/assets");
    } catch (err: any) {
      toast({ title: "LOGIN FAILED", description: err?.message || "UNABLE TO LOGIN" });
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signupEmail || !signupPassword || !signupGeneratedId) {
      return toast({ title: "MISSING FIELDS", description: "Please complete all signup fields" });
    }
    setSignupLoading(true);
    try {
      const payload = {
        email: signupEmail.toLowerCase(),
        password: signupPassword,
        generatedId: signupGeneratedId.toUpperCase(),
      };

      const res = await fetch("/api/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: 'include',  // 🔐 Allow cookies to be sent and stored
        body: JSON.stringify(payload),
      });

      const text = await res.text();
      const data = text ? JSON.parse(text) : null;

      if (!res.ok) {
        let errorMsg = data?.error || "Account creation failed";
        const errorCode = data?.code;

        if (errorCode === "EMAIL_ALREADY_EXISTS") {
          errorMsg = "This email is already registered. Please log in or use a different email.";
        } else if (errorCode === "ID_ALREADY_EXISTS") {
          errorMsg = "This ID is already taken. Please choose a different ID.";
        } else if (errorCode === "INVALID_EMAIL") {
          errorMsg = "Please enter a valid email address.";
        } else if (errorCode === "PASSWORD_TOO_SHORT") {
          errorMsg = "Password must be at least 6 characters.";
        } else if (errorCode === "WALLET_ERROR") {
          errorMsg = "Failed to generate wallet. Please try again.";
        } else if (res.status === 503) {
          errorMsg = "Service temporarily unavailable. Please try again later.";
        }

        throw new Error(errorMsg);
      }

      // 🔐 Do NOT store auth data in sessionStorage/localStorage
      // Session is now handled by the HttpOnly cookie automatically.

      // Clear all auth storage for security
      sessionStorage.removeItem("userEmail");
      sessionStorage.removeItem("userData");
      localStorage.removeItem("currentUser");

      window.dispatchEvent(new Event("userUpdated"));
      toast({ title: "ACCOUNT CREATED", description: `SIGNED UP AS ${signupEmail}` });
      navigate("/assets");
    } catch (err: any) {
      toast({ title: "SIGNUP FAILED", description: err?.message || "Unable to create account" });
    } finally {
      setSignupLoading(false);
    }
  };

  if (showWelcomeLoader && currentUser) return <WelcomeLoader />;

  if (mode === "signup") {
    return (
      <section className="min-h-screen flex items-center justify-center bg-background py-8">
        <div className="w-full max-w-md">
          <form onSubmit={handleSignup} className="space-y-6 bg-transparent rounded-lg border border-border p-6">
            <h2 className="text-2xl font-semibold mb-6 text-center">SIGN UP</h2>

            <div className="space-y-2">
              <label className="text-sm font-medium">EMAIL</label>
              <Input
                type="email"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">PASSWORD</label>
              <Input
                type="password"
                value={signupPassword}
                onChange={(e) => setSignupPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">GENERATED ID</label>
              <Input
                type="text"
                value={signupGeneratedId}
                onChange={(e) => setSignupGeneratedId(e.target.value)}
                placeholder="ID_XXXX"
                className="w-full"
              />
            </div>

            <Button type="submit" disabled={signupLoading} className="w-full h-10 text-base">
              {signupLoading ? "SIGNING UP…" : "SIGN UP"}
            </Button>

            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => setMode("login")}
                className="text-sm text-primary hover:underline"
              >
                Back to Login
              </button>
            </div>
          </form>
        </div>
      </section>
    );
  }

  // Login form
  return (
    <section className="min-h-screen flex items-center justify-center bg-background py-8">
      <div className="w-full max-w-md">
        <form onSubmit={handleLogin} className="space-y-6 bg-transparent rounded-lg border border-border p-6">
          <h2 className="text-2xl font-semibold mb-6 text-center">LOG IN</h2>

          <div className="space-y-2">
            <label className="text-sm font-medium">EMAIL OR GENERATED ID</label>
            <Input
              value={loginIdentifier}
              onChange={(e) => setLoginIdentifier(e.target.value)}
              type="text"
              placeholder="you@example.com or ID_..."
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">PASSWORD</label>
            <div className="relative">
              <Input
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                type={showLoginPassword ? "text" : "password"}
                placeholder="••••••••"
                className="w-full"
              />
              <button
                type="button"
                onClick={() => setShowLoginPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <Button type="submit" disabled={loginLoading} className="w-full h-10 text-base">
            {loginLoading ? "SIGNING IN…" : "LOG IN"}
          </Button>

          <div className="text-center mt-4">
            <button
              type="button"
              onClick={() => setMode("signup")}
              className="text-sm text-primary hover:underline"
            >
              Create New Account
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
